// hooks/useDateNavigation.ts
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useSelectedPeriod } from '@context/SelectedPeriodContext';

interface UseDateNavigationOptions {
    cutoffDay?: number; // defaults to last day of month
    initialDate?: Dayjs;
}

export const useDateNavigation = (options: UseDateNavigationOptions = {}) => {
    const { cutoffDay, initialDate } = options;
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedMonth: ctxMonth, selectedYear: ctxYear, setPeriod } = useSelectedPeriod();

    const today = dayjs();
    const lastDay = today.endOf('month').date();
    const cutoff = cutoffDay ?? lastDay;

    // Determine effective date
    let effectiveDate;
    if (today.date() < cutoff) {
        effectiveDate = today.subtract(1, 'month');
    } else {
        effectiveDate = today;
    }

    // Priority: URL param > context (user-selected) > initialDate > effective date
    const urlMonth = searchParams.get('month');
    const urlDate = useMemo(() => {
        if (urlMonth && /^\d{4}-\d{2}$/.test(urlMonth)) {
            const parsed = dayjs(urlMonth, 'YYYY-MM');
            if (parsed.isValid()) {
                return parsed;
            }
        }
        return null;
    }, [urlMonth]);

    const ctxDate = (ctxMonth !== null && ctxYear !== null)
        ? dayjs(new Date(ctxYear, ctxMonth, 1))
        : null;

    const startDate = urlDate ?? ctxDate ?? initialDate ?? effectiveDate;

    // State
    const [selectedMonth, setSelectedMonth] = useState(startDate.month());
    const [selectedYear, setSelectedYear] = useState(startDate.year());

    // Computed values
    const selectedPeriod = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    const lastSelectablePeriod = `${effectiveDate.year()}-${String(effectiveDate.month() + 1).padStart(2, '0')}`;
    const isNextDisabled = selectedPeriod >= lastSelectablePeriod;
    const monthYearLabel = dayjs(`${selectedYear}-${selectedMonth + 1}`).format('MMMM YYYY');
    const pickerValue = dayjs(new Date(selectedYear, selectedMonth, 1));

    // Sync URL on mount if ?month param is absent so breadcrumb captures the correct path.
    useEffect(() => {
        if (!urlMonth) {
            const period = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('month', period);
                return next;
            }, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const syncUrl = useCallback((month: number, year: number) => {
        const period = `${year}-${String(month + 1).padStart(2, '0')}`;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('month', period);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Navigation
    const navigateMonth = (direction: number) => {
        let month = selectedMonth + direction;
        let year = selectedYear;

        if (month > 11) {
            month = 0;
            year++;
        } else if (month < 0) {
            month = 11;
            year--;
        }

        setSelectedMonth(month);
        setSelectedYear(year);
        setPeriod(month, year);
        syncUrl(month, year);
    };

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            setSelectedMonth(date.month());
            setSelectedYear(date.year());
            setPeriod(date.month(), date.year());
            syncUrl(date.month(), date.year());
        }
    };

    return {
        selectedMonth,
        selectedYear,
        selectedPeriod,
        monthYearLabel,
        pickerValue,
        isNextDisabled,
        navigateMonth,
        handleDateChange,
    };
};